import React, { useState, useEffect } from "react";
import {
  Plus,
  Settings,
  Clipboard,
  Eye,
  X,
  MessageCircle,
  HelpCircle,
  Trash2,
  ChevronDown,
  AlertTriangle,
  ArrowRight,
  Minus,
} from "lucide-react";
import toast from "react-hot-toast";
import ApiService from "../../services/ApiService";
import useBillingStore from "../../store/useBillingStore";

function WidgetManager() {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewWidget, setPreviewWidget] = useState(null);
  const [widgetForm, setWidgetForm] = useState({
    type: "faq",
    title: "Frequently Asked Questions",
    primaryColor: "#3B82F6",
    secondaryColor: "#6366F1",
    position: "bottom-right",
    greeting: "Hello! How can we help you today?",
    footerEnabled: true,
  });
  const [editingWidgetId, setEditingWidgetId] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    primaryColor: "",
    secondaryColor: "",
    position: "bottom-right",
    accordionIconStyle: "plus-minus",
    footerEnabled: true,
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null);
  const { billingInfo, fetchBillingInfo } = useBillingStore();
  const [widgetType, setWidgetType] = useState("");
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [hoveredCard, setHoveredCard] = React.useState(null);

  useEffect(() => {
    localStorage.setItem("loading", loading);
  }, [loading]);

  useEffect(() => {
    loadWidgets();
    fetchBillingInfo();
  }, [fetchBillingInfo]);



  // Compute which billing quotas are exceeded and prepare friendly messages
  const getExceededDetails = () => {
    // Only show tokens and widget view counts for Widget Manager
    if (!billingInfo || !billingInfo.limits || !billingInfo.usage) return [];
    const details = [];
    const tokensUsed = billingInfo.usage?.tokens;
    const tokensLimit = billingInfo.limits?.tokens;
    if (
      typeof tokensUsed === "number" &&
      typeof tokensLimit === "number" &&
      tokensUsed >= tokensLimit
    ) {
      details.push({
        key: "tokens",
        title: "AI Tokens",
        used: tokensUsed,
        limit: tokensLimit,
        why: "AI tokens exhausted — chat and AI-powered widgets are limited. Upgrade to restore AI features.",
      });
    }
    // Some backends may report widget views under different keys; check common variants
    const viewsUsed =
      billingInfo.usage?.widget_views ??
      billingInfo.usage?.views ??
      billingInfo.usage?.view_count;
    const viewsLimit =
      billingInfo.limits?.widget_views ??
      billingInfo.limits?.views ??
      billingInfo.limits?.view_count;
    if (
      typeof viewsUsed === "number" &&
      typeof viewsLimit === "number" &&
      viewsUsed >= viewsLimit
    ) {
      details.push({
        key: "widget_views",
        title: "Widget Views",
        used: viewsUsed,
        limit: viewsLimit,
        why: "Your widget view quota is exhausted — users may not be able to load or see widgets. Upgrade to increase view limits.",
      });
    }
    return details;
  };

  const loadWidgets = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getWidgets();
      setWidgets(data.widgets || []);
    } catch (error) {
      console.error("Failed to load widgets:", error);
      toast.error("Failed to load widgets");
    } finally {
      setLoading(false);
    }
  };

  const copyEmbedCode = (embedCode) => {
    navigator.clipboard.writeText(embedCode);
    toast.success("Embed code copied to clipboard!");
  };

  const getPreviewCode = (widget) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    return `<script src="${baseUrl}/api/widgets/${widget?.widget_type}-widget.js?organization_id=${widget?.organization_id}&widget_id=${widget?.widget_id}"></script>`;
  };

  const handleCreateWidget = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const configuration = {
        title: widgetForm.title,
        primaryColor: widgetForm.primaryColor,
        secondaryColor: widgetForm.secondaryColor,
        position: widgetForm.position,
        greeting: widgetForm.greeting,
        supportEnabled: widgetForm.supportEnabled,
        footerEnabled: widgetForm.footerEnabled,
      };

      const result = await ApiService.createWidget(
        widgetForm.type,
        configuration
      );

      if (result.success) {
        toast.success("Widget created successfully!");
        setShowCreateModal(false);
        setWidgetForm({
          type: "faq",
          title: "Frequently Asked Questions",
          primaryColor: "#3B82F6",
          secondaryColor: "#6366F1",
          position: "bottom-right",
          greeting: "Hello! How can we help you today?",
          supportEnabled: true,
          footerEnabled: true,
        });
        loadWidgets();
      } else {
        toast.error("Failed to create widget");
      }
    } catch (error) {
      console.error("Widget creation error:", error);
      toast.error("Failed to create widget");
    } finally {
      setIsSubmitting(false);
    }
  };

  const showPreview = (widget) => {
    setPreviewWidget(widget);
    setShowPreviewModal(true);
  };
  const getWidgetTypeIcon = (type) => {
    return type === "chat" ? MessageCircle : HelpCircle;
  };

  const handleDeleteConfirm = async (widgetId) => {
    setShowDeleteConfirm(false);
    setShowEditModal(false);
    try {
      await ApiService.deleteWidget(widgetId);
      // If we reach here, deletion was successful
      toast.success("Widget deleted successfully");

      // Update local state instead of reloading
      setWidgets((prevWidgets) =>
        prevWidgets.filter((widget) => widget.widget_id !== widgetId)
      );
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.message || "Failed to delete widget");
    }
  };

  const startEdit = (widget) => {
    setSelectedWidget(widget);
    setShowEditModal(true);
    setEditingWidgetId(widget.widget_id);
    setEditForm({
      title: widget.configuration?.title || "",
      primaryColor: widget.configuration?.primaryColor || "#3B82F6",
      secondaryColor: widget.configuration?.secondaryColor || "#6366F1",
      position:
        widget.widget_type === "chat"
          ? widget.configuration?.position || "bottom-right"
          : undefined,
      supportEnabled:
        widget.widget_type === "faq"
          ? widget.configuration?.supportEnabled ?? true
          : undefined,
      contactPosition:
        widget.widget_type === "faq"
          ? widget.configuration?.contactPosition || "bottom"
          : undefined,
      accordionIconStyle:
        widget.widget_type === "faq"
          ? widget.configuration?.accordionIconStyle || "plus-minus"
          : undefined,
      footerEnabled: widget.configuration?.footerEnabled ?? true,
    });
  };

  const saveEdit = async (widget_id) => {
    try {
      const widget = widgets.find((w) => w.widget_id === widget_id);
      const updateData = {
        title: editForm.title,
        primaryColor: editForm.primaryColor,
        secondaryColor: editForm.secondaryColor,
        footerEnabled: editForm.footerEnabled,
      };
      if (widget.widget_type === "chat") {
        updateData.position = editForm.position;
        // Enterprise branding fields
        updateData.brandingText = editForm.brandingText;
        updateData.logo = editForm.logo;
        updateData.showLogo = editForm.showLogo;
        updateData.chatIcon = editForm.chatIcon;
        updateData.headerBackgroundColor = editForm.headerBackgroundColor;
        updateData.backgroundColor = editForm.backgroundColor;
        // Custom header fields
        updateData.enableCustomHeader = editForm.enableCustomHeader;
        updateData.headerText = editForm.headerText;
        updateData.chatIconColor = editForm.chatIconColor;
      }
      if (widget.widget_type === "faq") {
        updateData.supportEnabled = editForm.supportEnabled;
        updateData.contactPosition = editForm.contactPosition;
        updateData.accordionIconStyle = editForm.accordionIconStyle;
      }
      await ApiService.updateWidgetSettings(widget_id, updateData);
      toast.success("Widget updated!");
      setEditingWidgetId(null);
      setShowEditModal(false);
      loadWidgets();
    } catch {
      toast.error("Failed to update widget");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[90vh]">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent leading-tight">Widget Manager</h2>
          <p className="text-gray-600 mt-0.5 text-sm sm:text-base">
            Create and manage widgets for your website
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex h-10 items-center justify-center px-4 bg-black text-white font-medium rounded-lg shadow-lg hover:shadow-xl hover:bg-gray-800 transition-all duration-200"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Widget
        </button>
      </div>
      {billingInfo &&
        billingInfo.limits &&
        billingInfo.usage &&
        (() => {
          const exceeded = getExceededDetails();
          if (!exceeded || exceeded.length === 0) return null;
          return (
            <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50/60 backdrop-blur p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div className="h-9 w-9 rounded-lg bg-gray-100 flex items-center justify-center border border-gray-200">
                    <AlertTriangle className="h-5 w-5 text-black" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <h4 className="text-base sm:text-lg font-semibold text-black">
                        Usage limit reached
                      </h4>
                      <p className="text-sm text-gray-700/90 mt-0.5">
                        You&apos;ve reached the following monthly quota
                        {exceeded.length > 1 ? "s" : ""}. Upgrade to increase
                        limits and re-enable affected features.
                      </p>
                    </div>
                    <a
                      href="/billing"
                      className="inline-flex items-center gap-2 rounded-lg bg-black text-white px-3 py-2 text-sm font-semibold shadow-sm hover:bg-gray-800 transition-colors"
                    >
                      View billing & upgrade
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>

                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {exceeded.map((d) => {
                      const percent =
                        d.limit > 0
                          ? Math.min(100, Math.round((d.used / d.limit) * 100))
                          : 100;
                      return (
                        <div
                          key={d.key}
                          className="rounded-lg border border-gray-200 bg-white/60 p-3"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium uppercase tracking-wide text-gray-700/80">
                              {d.title}
                            </span>
                            <span className="text-[11px] font-semibold text-black bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">
                              {d.used} / {d.limit}
                            </span>
                          </div>
                          <div className="mt-2 h-2 rounded-full bg-gray-100 border border-gray-200 overflow-hidden">
                            <div
                              className="h-full bg-black"
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                          <p className="mt-2 text-xs text-gray-700/90 leading-relaxed">
                            {d.why}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      {/* Widget Grid */}
      {widgets.length === 0 ? (
        <div className="text-center py-16">
          <div className="relative mx-auto w-24 h-24 mb-6">
            <div className="absolute inset-0 bg-gray-100 rounded-2xl"></div>
            <div className="absolute inset-2 bg-white rounded-xl flex items-center justify-center">
              <Settings className="h-8 w-8 text-black" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No widgets yet
          </h3>
          <p className="text-gray-500 mb-8 max-w-md mx-auto">
            Create your first widget to get started.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex h-10 items-center justify-center px-6 bg-black text-white font-medium rounded-lg shadow-lg hover:shadow-xl hover:bg-gray-800 transition-all duration-200"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Widget
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {widgets.map((widget) => {
            const Icon = getWidgetTypeIcon(widget.widget_type);
            const isHovered = hoveredCard === widget.widget_id;

            return (
              <div
                key={widget.widget_id}
                onMouseEnter={() => setHoveredCard(widget.widget_id)}
                onMouseLeave={() => setHoveredCard(null)}
                className="group relative bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300 overflow-hidden"
                style={{
                  transform: isHovered ? "translateY(-8px)" : "translateY(0)",
                }}
              >
                {/* Gradient overlay */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500"
                  style={{
                    background: `rgba(0,0,0,0)`,
                  }}
                />

                {/* Content */}
                <div className="relative p-4 sm:p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-black rounded-lg shadow-md group-hover:scale-110 transition-transform duration-300">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 text-sm sm:text-base capitalize">
                          {widget.widget_type}
                        </h3>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Widget</p>
                      </div>
                    </div>

                    {/* <button className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200">
                      <MoreVertical className="h-5 w-5 text-gray-400" />
                    </button> */}
                  </div>

                  {/* Details */}
                  <div className="space-y-2 mb-4">
                    {widget.widget_type === "chat" && (
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg group-hover:bg-gray-100 transition-all duration-300">
                        <span className="text-xs font-medium text-gray-500">
                          Position
                        </span>
                        <span className="text-xs font-bold text-gray-900 capitalize">
                          {widget.configuration?.position?.replace("-", " ") ||
                            "bottom right"}
                        </span>
                      </div>
                    )}

                    {widget.widget_type === "faq" && (
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg group-hover:bg-gray-100 transition-all duration-300">
                        <span className="text-xs font-medium text-gray-500">
                          Support Button
                        </span>
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${widget.configuration?.supportEnabled
                            ? "bg-black text-white"
                            : "bg-gray-200 text-gray-600"
                            }`}
                        >
                          {widget.configuration?.supportEnabled
                            ? "Enabled"
                            : "Disabled"}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-xs font-medium text-gray-500">
                        Theme
                      </span>
                      <div className="flex items-center gap-1">
                        <div
                          className="w-4 h-4 rounded-full border border-white"
                          style={{
                            backgroundColor:
                              widget.configuration?.primaryColor || "#3B82F6",
                          }}
                        />
                        <div
                          className="w-4 h-4 rounded-full border border-white"
                          style={{
                            backgroundColor:
                              widget.configuration?.secondaryColor || "#6366F1",
                          }}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-xs font-medium text-gray-500">
                        Created
                      </span>
                      <span className="text-xs font-bold text-gray-900">
                        {new Date(widget.created_at).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          }
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-2">
                    <button
                      onClick={() => startEdit(widget)}
                      className="w-full flex items-center justify-center gap-2 px-4 h-10 bg-black text-white text-sm font-bold rounded-lg transition-all duration-200"
                    >
                      <Settings className="h-4 w-4" />
                      Manage {widget.widget_type === "faq" ? "FAQ" : "Chat"}
                    </button>

                    <div className="grid grid-cols-1 gap-2">
                      {/* <button
                        onClick={() => showPreview(widget)}
                        className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200"
                      >
                        <Eye className="h-4 w-4" />
                        Preview
                      </button> */}

                      <button
                        onClick={() => {
                          setWidgetType(widget.widget_type);
                          showPreview(widget);
                        }}
                        className="flex items-center justify-center gap-2 px-4 h-9 bg-white border border-gray-200 text-black text-sm font-bold rounded-lg hover:border-black transition-all duration-200"
                      >
                        <Clipboard className="h-3.5 w-3.5" />
                        Copy Code
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-5 py-3 border-b border-gray-200 bg-gray-50/50">
          <h3 className="font-bold text-gray-900 text-sm">Widget Types</h3>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="group border border-gray-200 rounded-2xl p-6 hover:border-black hover:shadow-lg transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-black rounded-lg mr-3 group-hover:scale-110 transition-transform duration-200">
                  <HelpCircle className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900">
                  FAQ Widget
                </h4>
              </div>
              <p className="text-gray-600 mb-4">
                Display frequently asked questions in an interactive accordion
                widget.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-black rounded-full mr-2"></div>
                  Searchable FAQ interface
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-black rounded-full mr-2"></div>
                  SEO FAQ Schema included
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-black rounded-full mr-2"></div>
                  Customizable styling
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-black rounded-full mr-2"></div>
                  Mobile responsive
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-black rounded-full mr-2"></div>
                  AI traffic tracking
                </li>
              </ul>
            </div>

            <div className="group border border-gray-200 rounded-2xl p-6 hover:border-black hover:shadow-lg transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-black rounded-lg mr-3 group-hover:scale-110 transition-transform duration-200">
                  <MessageCircle className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900">
                  Chat Widget
                </h4>
              </div>
              <p className="text-gray-600 mb-4">
                AI-powered chat widget that provides instant answers based on
                your content.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-black rounded-full mr-2"></div>
                  AI-powered responses
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-black rounded-full mr-2"></div>
                  Real-time chat interface
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-black rounded-full mr-2"></div>
                  Context-aware answers
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-black rounded-full mr-2"></div>
                  Support query capture
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-black rounded-full mr-2"></div>
                  Email notifications
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* Create Widget Modal - Redesigned with Preview */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 ">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-3 sm:p-5">
              <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 p-4 sm:p-8 my-4">
                {/* Left: Form */}
                <div className="md:col-span-1">
                  <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-800">
                      Create New Widget
                    </h1>
                    <button
                      className="text-gray-400 hover:text-gray-600"
                      onClick={() => setShowCreateModal(false)}
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                  <form onSubmit={handleCreateWidget}>
                    <div className="mb-6">
                      <div className={`grid gap-4 ${widgetForm.type === "chat" ? "grid-cols-2" : "grid-cols-1"}`}>
                        <div>
                          <label
                            className="block text-sm font-medium text-gray-700 mb-2"
                            htmlFor="widget-type"
                          >
                            Widget Type
                          </label>
                          <div className="relative">
                            <select
                              id="widget-type"
                              value={widgetForm.type}
                              onChange={(e) => {
                                const newType = e.target.value;
                                setWidgetForm({
                                  ...widgetForm,
                                  type: newType,
                                  title:
                                    newType === "chat"
                                      ? "Customer Support"
                                      : "Frequently Asked Questions",
                                });
                              }}
                              className="w-full bg-white border border-gray-300 rounded-md py-3 px-4 pr-10 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-gray-500 appearance-none cursor-pointer"
                            >
                              <option value="faq">FAQ Widget</option>
                              <option value="chat">
                                Chat Widget
                              </option>
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                          </div>
                        </div>
                        {widgetForm.type === "chat" && (
                          <div>
                            <label
                              className="block text-sm font-medium text-gray-700 mb-2"
                              htmlFor="position"
                            >
                              Position
                            </label>
                            <div className="relative">
                              <select
                                id="position"
                                value={widgetForm.position}
                                onChange={(e) =>
                                  setWidgetForm({
                                    ...widgetForm,
                                    position: e.target.value,
                                  })
                                }
                                className="w-full bg-white border border-gray-300 rounded-md py-3 px-4 pr-10 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-gray-500 appearance-none cursor-pointer"
                              >
                                <option value="bottom-right">Bottom Right</option>
                                <option value="bottom-left">Bottom Left</option>
                                <option value="top-right">Top Right</option>
                                <option value="top-left">Top Left</option>
                              </select>
                              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mb-6">
                      <label
                        className="block text-sm font-medium text-gray-700 mb-2"
                        htmlFor="title"
                      >
                        Title
                      </label>
                      <input
                        id="title"
                        type="text"
                        value={widgetForm.title}
                        onChange={(e) =>
                          setWidgetForm({ ...widgetForm, title: e.target.value })
                        }
                        className="w-full bg-white border border-gray-300 rounded-md py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-gray-500"
                        required
                      />
                    </div>
                    <div className="mb-6">
                      <p className="block text-sm font-medium text-gray-700 mb-2">
                        Primary & Secondary Color
                      </p>
                      <div className="flex space-x-6">
                        <div className="flex-1">
                          <label
                            className="block text-xs text-gray-500 text-center mb-1"
                            htmlFor="primary-color"
                          >
                            Primary
                          </label>
                          <input
                            id="primary-color"
                            type="color"
                            value={widgetForm.primaryColor}
                            onChange={(e) =>
                              setWidgetForm({
                                ...widgetForm,
                                primaryColor: e.target.value,
                              })
                            }
                            className="w-full h-12 p-1 border border-gray-300 rounded-md cursor-pointer"
                          />
                          <div className="mt-2 text-center text-sm text-gray-600">
                            {widgetForm.primaryColor.toUpperCase()}
                          </div>
                        </div>
                        <div className="flex-1">
                          <label
                            className="block text-xs text-gray-500 text-center mb-1"
                            htmlFor="secondary-color"
                          >
                            Secondary
                          </label>
                          <input
                            id="secondary-color"
                            type="color"
                            value={widgetForm.secondaryColor}
                            onChange={(e) =>
                              setWidgetForm({
                                ...widgetForm,
                                secondaryColor: e.target.value,
                              })
                            }
                            className="w-full h-12 p-1 border border-gray-300 rounded-md cursor-pointer"
                          />
                          <div className="mt-2 text-center text-sm text-gray-600">
                            {widgetForm.secondaryColor.toUpperCase()}
                          </div>
                        </div>
                      </div>
                    </div>
                    {widgetForm.type === "chat" && (
                      <>
                        <div className="mb-6">
                          <label
                            className="block text-sm font-medium text-gray-700 mb-2"
                            htmlFor="greeting"
                          >
                            Greeting Message
                          </label>
                          <input
                            id="greeting"
                            type="text"
                            value={widgetForm.greeting}
                            onChange={(e) =>
                              setWidgetForm({
                                ...widgetForm,
                                greeting: e.target.value,
                              })
                            }
                            className="w-full bg-white border border-gray-300 rounded-md py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-gray-500"
                          />
                        </div>
                        <div className="mb-6">
                          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <label className="flex items-start justify-between cursor-pointer">
                              <div className="flex-1 pr-4">
                                <p className="text-sm font-medium text-gray-800 mb-1">
                                  Show Footer
                                </p>
                                <p className="text-xs text-gray-600 leading-relaxed">
                                  Enable to show &apos;we run on [Logo]&apos; footer in the chat widget
                                </p>
                              </div>
                              <div className="relative flex-shrink-0">
                                <input
                                  type="checkbox"
                                  checked={widgetForm.footerEnabled}
                                  onChange={() =>
                                    setWidgetForm({
                                      ...widgetForm,
                                      footerEnabled: !widgetForm.footerEnabled,
                                    })
                                  }
                                  className="sr-only"
                                />
                                <div
                                  className={`w-12 h-6 ${widgetForm.footerEnabled
                                    ? "bg-black"
                                    : "bg-gray-300"
                                    } rounded-full transition-colors duration-200`}
                                ></div>
                                <div
                                  className="dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 shadow-sm"
                                  style={{
                                    transform: widgetForm.footerEnabled
                                      ? "translateX(24px)"
                                      : "translateX(0)",
                                  }}
                                ></div>
                              </div>
                            </label>
                          </div>
                        </div>
                      </>
                    )}
                    {widgetForm.type === "faq" && (
                      <div className="mb-6">
                        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <label className="flex items-start justify-between cursor-pointer">
                            <div className="flex-1 pr-4">
                              <p className="text-sm font-medium text-gray-800 mb-1">
                                Enable Contact Support Button
                              </p>
                              <p className="text-xs text-gray-600 leading-relaxed">
                                When enabled, users can contact your support team
                                directly from the FAQ widget
                              </p>
                            </div>
                            <div className="relative flex-shrink-0">
                              <input
                                type="checkbox"
                                checked={widgetForm.supportEnabled}
                                onChange={() =>
                                  setWidgetForm({
                                    ...widgetForm,
                                    supportEnabled: !widgetForm.supportEnabled,
                                  })
                                }
                                className="sr-only"
                              />
                              <div
                                className={`w-12 h-6 ${widgetForm.supportEnabled
                                  ? "bg-black"
                                  : "bg-gray-300"
                                  } rounded-full transition-colors duration-200`}
                              ></div>
                              <div
                                className="dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 shadow-sm"
                                style={{
                                  transform: widgetForm.supportEnabled
                                    ? "translateX(100%)"
                                    : "translateX(0)",
                                }}
                              ></div>
                            </div>
                          </label>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-end space-x-4 mt-8">
                      <button
                        type="button"
                        className="px-6 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
                        onClick={() => setShowCreateModal(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2.5 bg-black text-white font-semibold rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Submitting..." : "Create Widget"}
                      </button>
                    </div>

                  </form>
                </div>
                {/* Right: Live Preview */}
                <div className="md:col-span-1 p-3 sm:p-4 border border-gray-200 rounded-lg bg-gray-50 flex flex-col">
                  <div
                    className="flex items-center justify-center min-h-[500px] py-4"
                  >
                    <div className="max-w-full w-full flex justify-center">
                      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 w-full max-w-full overflow-visible">
                        <div className="p-0">
                          {widgetForm.type === "faq" ? (
                            <div className="space-y-4 px-4 py-4 md:px-6 md:py-6">
                              <div className="text-center pt-2 mb-2">
                                <div
                                  className="text-xs font-semibold tracking-wider mb-1"
                                  style={{
                                    color: widgetForm.primaryColor || "#3B82F6",
                                  }}
                                >
                                  GOT QUESTIONS?
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 mb-2">
                                  {widgetForm.title ||
                                    "Frequently Asked Question"}
                                </h3>
                              </div>
                              <div className="border-t border-gray-200 my-3"></div>
                              {/* Search Bar */}
                              <div className="mb-2">
                                <input
                                  type="text"
                                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                                  placeholder="Search frequently asked questions..."
                                  disabled
                                />
                              </div>
                              {/* FAQ Items (reduced for compact preview) */}
                              <div className="space-y-2">
                                <div className="text-sm">
                                  <div className="font-normal text-gray-700 flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg shadow-sm">
                                    What is the AEO Platform and how does it work?
                                  </div>
                                </div>
                                <div className="text-sm">
                                  <div className="font-normal text-gray-700 flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg shadow-sm">
                                    How does the AEO Platform improve my website&apos;s
                                    SEO?
                                  </div>
                                </div>
                                <div className="text-sm">
                                  <div className="font-normal text-gray-700 flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg shadow-sm">
                                    What is RAG technology and how does the AEO
                                    Platform use it?
                                  </div>
                                </div>
                              </div>
                              <div className="border-t border-gray-200 my-3"></div>
                              <div className="pt-2 text-center">
                                <div className="flex items-center justify-center mb-2">
                                  <div className="flex items-center justify-center space-x-1">
                                    <button className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600 disabled:opacity-50">
                                      Prev
                                    </button>
                                    <button
                                      className="px-2 py-1 text-xs text-white rounded-full w-6 h-6 flex items-center justify-center font-medium"
                                      style={{
                                        background: `linear-gradient(90deg, ${widgetForm.primaryColor || "#E91E63"
                                          }, ${widgetForm.secondaryColor || "#1A1A2E"
                                          })`,
                                        border: "none",
                                      }}
                                    >
                                      1
                                    </button>
                                    <button className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded">
                                      2
                                    </button>
                                    <button className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded">
                                      3
                                    </button>
                                    <button className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800">
                                      Next
                                    </button>
                                  </div>
                                </div>
                                {widgetForm.supportEnabled && (
                                  <div className="mt-2 text-center">
                                    <div className="text-xs text-gray-500 mb-2">
                                      Can&apos;t find what you&apos;re looking for?
                                    </div>
                                    <button
                                      className="px-4 py-2 text-xs text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                                      style={{
                                        background: `linear-gradient(90deg, ${widgetForm.primaryColor || "#E91E63"
                                          }, ${widgetForm.secondaryColor || "#1A1A2E"
                                          })`,
                                      }}
                                    >
                                      Contact Our Support Team
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="w-full max-w-[340px] mx-auto h-[500px] bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden flex flex-col">
                                {/* Header */}
                                <div
                                  className="flex items-center justify-between px-5 py-3 text-white"
                                  style={{
                                    background: `linear-gradient(135deg, ${widgetForm.primaryColor || "#3B82F6"
                                      }, ${widgetForm.secondaryColor || "#6366F1"})`,
                                  }}
                                >
                                  <span className="font-semibold text-base">
                                    {widgetForm.title || "Customer Support"}
                                  </span>
                                  <button className="p-1.5 hover:opacity-80 transition-opacity">
                                    {/* Close Icon */}
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="20"
                                      height="20"
                                      viewBox="0 0 24 24"
                                      stroke="white"
                                      strokeWidth="2"
                                      fill="none"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <line x1="18" y1="6" x2="6" y2="18" />
                                      <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                  </button>
                                </div>

                                {/* Greeting */}
                                <div className="px-3 sm:px-5 py-4">
                                  <div className="bg-white border border-gray-200 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-gray-700 text-xs sm:text-sm shadow-sm">
                                    {widgetForm.greeting ||
                                      "Hello! How can we help you today?"}
                                  </div>
                                </div>

                                {/* Chat area (empty for preview) */}
                                <div className="flex-1 flex flex-col justify-end px-3 sm:px-5"></div>

                                <div className="flex items-center gap-2 px-3 sm:px-5 mb-3 text-xs sm:text-sm">
                                  <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="flex-shrink-0"
                                  >
                                    <path
                                      d="M12 12c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19c0 0.55 0.45 1 1 1h12c0.55 0 1-0.45 1-1v-1.5c0-2.33-4.67-3.5-7-3.5z"
                                      fill={widgetForm.primaryColor}
                                    />
                                  </svg>
                                  <span
                                    className="font-medium cursor-pointer bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] bg-clip-text text-transparent"
                                    style={{
                                      "--primary": widgetForm.primaryColor || "#3B82F6",
                                      "--secondary": widgetForm.secondaryColor || "#6366F1",
                                      backgroundImage: `linear-gradient(90deg, ${widgetForm.primaryColor || "#3B82F6"}, ${widgetForm.secondaryColor || "#6366F1"})`,
                                      WebkitBackgroundClip: "text",
                                      WebkitTextFillColor: "transparent",
                                      backgroundClip: "text",
                                      color: "transparent",
                                    }}
                                  >
                                    Talk to Human
                                  </span>
                                </div>
                                <div className="relative w-full px-3 sm:px-5 pb-4">
                                  <input
                                    type="text"
                                    placeholder="Type your message here..."
                                    className="w-full text-xs sm:text-sm bg-gray-50 border border-gray-200 rounded-full pl-3 sm:pl-4 pr-10 sm:pr-12 py-2 sm:py-2.5 outline-none"
                                    disabled
                                  />
                                  <button
                                    className="absolute right-4 sm:right-6 top-1/3 -translate-y-1/2 w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shadow-sm "
                                    style={{
                                      background: `linear-gradient(90deg, ${widgetForm.primaryColor || "#3B82F6"
                                        }, ${widgetForm.secondaryColor || "#6366F1"
                                        })`,
                                    }}
                                    disabled
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-send-horizontal-icon lucide-send-horizontal"><path d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z" /><path d="M6 12h16" /></svg>
                                  </button>
                                </div>
                                {widgetForm.footerEnabled && (
                                  <div className="px-5 py-2.5 bg-white border-t border-gray-100 flex items-center justify-center gap-1.5 text-[10px] text-gray-400">
                                    <span className="font-medium">we run on</span>
                                    <img src="/logo/uttik_logo_b.png" className="h-4 w-auto object-contain" alt="Uttik Logo" />
                                  </div>
                                )}
                              </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Widget Preview Modal */}
      {
        showPreviewModal && previewWidget && (
          <div className="fixed inset-0 z-50">
            <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
            <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-5">
              <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="p-3 sm:p-5">
                  <div className="flex justify-between items-center mb-5">
                    <h3 className="text-xl font-semibold text-gray-900">
                      Embed Code
                    </h3>
                    <button
                      onClick={() => setShowPreviewModal(false)}
                      className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <div>
                    {/* Preview iframe */}

                    {/* Embed code */}
                    <div className="space-y-4">
                      {/* Script Code Display */}
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                          JavaScript Embed Code
                        </label>
                        <div className="bg-gray-50 text-black border border-gray-200 p-2 sm:p-4 rounded-lg font-mono text-[10px] sm:text-sm overflow-x-auto">
                          <pre className="whitespace-pre-wrap break-all sm:whitespace-pre sm:break-normal">{getPreviewCode(previewWidget)}</pre>
                        </div>
                      </div>

                      {/* Iframe Code Display */}
                      {
                        widgetType === "faq" && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                              Iframe Embed Code (SEO Optimized)
                            </label>
                            <div className="bg-gray-50 text-black border border-gray-200 p-2 sm:p-4 rounded-lg font-mono text-[10px] sm:text-sm overflow-x-auto">
                              <pre className="whitespace-pre-wrap break-all sm:whitespace-pre sm:break-normal">{`<iframe
  src="${process.env.REACT_APP_BACKEND_URL}/api/widgets/faq-widget-embed?organization_id=${previewWidget.organization_id}&widget_id=${previewWidget.widget_id}"
  scrolling="no"
  title="FAQ Widget"
  style="width: 100%; min-height: 990px; height: 100%; border: none"
></iframe>`}</pre>
                            </div>
                          </div>
                        )
                      }

                      {/* Copy Buttons */}
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-x-[10px]">
                        <button
                          onClick={() => copyEmbedCode(getPreviewCode(previewWidget))}
                          className="w-full flex items-center justify-center px-3 sm:px-4 py-2.5 sm:py-3 bg-white text-black text-sm sm:text-base font-medium rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-200"
                          title="Use This Script Only When SEO Is Not Required"
                        >
                          <Clipboard className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                          Copy Script
                        </button>

                        {
                          widgetType === 'faq' && (
                            <button
                              onClick={() => {
                                const url = `${process.env.REACT_APP_BACKEND_URL}/api/widgets/faq-widget-embed?organization_id=${previewWidget.organization_id}&widget_id=${previewWidget.widget_id}`;
                                const iframe = `<iframe
  src="${url}"
  scrolling="no"
  title="FAQ Widget"
  style="width: 100%; min-height: 990px; height: 100%; border: none"
></iframe>`;
                                copyEmbedCode(iframe);
                              }}
                              className="w-full flex items-center justify-center px-3 sm:px-4 py-2.5 sm:py-3 bg-white text-black text-sm sm:text-base font-medium rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-200"
                              title="Use This Iframe for SEO-Optimized Embedding"
                            >
                              <Clipboard className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                              Copy Iframe
                            </button>
                          )
                        }
                      </div>

                      <div className="mt-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
                        <h5 className="font-medium text-black mb-2 text-sm sm:text-base">
                          Features Included:
                        </h5>
                        <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                          <li>• SEO FAQ Schema for better search visibility</li>
                          <li>• AI traffic tracking and analytics</li>
                          <li>• Mobile responsive design</li>
                          <li>• Auto-adapts to your website styling</li>
                          <li>• Cross-origin support</li>
                        </ul>
                      </div>

                      {/* Info Box explaining the difference */}
                      {
                        widgetType === "faq" && (
                          <div className="mt-4 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <h5 className="font-medium text-black mb-2 flex items-center text-sm sm:text-base">
                              <svg className="h-4 w-4 sm:h-5 sm:w-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                              Which embed method should I use?
                            </h5>
                            <div className="text-xs sm:text-sm text-gray-700 space-y-2">
                              <p><strong>Use Script:</strong> For dynamic loading and smaller initial page size. Not SEO-friendly as content loads via JavaScript.</p>
                              <p><strong>Use Iframe (Recommended):</strong> For better SEO as search engines can crawl the FAQ content. Provides server-side rendering with progressive enhancement.</p>
                            </div>
                          </div>
                        )
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {
        showEditModal && !showDeleteConfirm && (
          <div className="fixed inset-0 z-50">
            <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-3 sm:p-5">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveEdit(editingWidgetId);
                  }} className="bg-white rounded-xl shadow-lg w-full max-w-4xl p-4 sm:p-8 relative my-4">
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <h1 className="text-lg sm:text-2xl font-bold text-gray-800">
                      Manage Widget
                    </h1>
                  </div>
                  <button
                    className="text-gray-400 hover:text-gray-600 absolute right-3 top-3"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingWidgetId(null);
                    }}
                  >
                    <X className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>

                  <div className=" grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">

                    {/* Left: Edit Form */}
                    <div className="md:col-span-1">

                      <div
                        className="space-y-6"
                      >
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Title
                          </label>
                          <input
                            type="text"
                            value={editForm.title}
                            onChange={(e) =>
                              setEditForm({ ...editForm, title: e.target.value })
                            }
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-gray-500/50 focus:border-gray-500 transition-all duration-200"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Primary & Secondary color
                          </label>
                          <div className="flex flex-row gap-4 flex-nowrap">
                            <div className="flex-1 flex flex-col items-center">
                              <span className="text-xs text-gray-500 mb-1">
                                Primary
                              </span>
                              <input
                                type="color"
                                value={editForm.primaryColor}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    primaryColor: e.target.value,
                                  })
                                }
                                className="w-full h-12 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-gray-500/50 focus:border-gray-500 transition-all duration-200"
                                style={{ minWidth: 0, maxWidth: "100px" }}
                              />
                            </div>
                            <div className="flex-1 flex flex-col items-center">
                              <span className="text-xs text-gray-500 mb-1">
                                Secondary
                              </span>
                              <input
                                type="color"
                                value={editForm.secondaryColor}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    secondaryColor: e.target.value,
                                  })
                                }
                                className="w-full h-12 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-gray-500/50 focus:border-gray-500 transition-all duration-200"
                                style={{ minWidth: 0, maxWidth: "100px" }}
                              />
                            </div>
                          </div>
                          <div className="w-full flex justify-center">
                            <span className="mt-3 text-xs text-gray-400 flex items-center gap-1 whitespace-nowrap">
                              Click the
                              <span
                                className="inline-flex items-center align-middle relative"
                                style={{ top: "-1px" }}
                              >
                                <Eye className="h-5 w-5" />
                              </span>
                              button to preview your color changes.
                            </span>
                          </div>
                        </div>
                        {(() => {
                          const widget = widgets.find(
                            (w) => w.widget_id === editingWidgetId
                          );
                          if (widget?.widget_type === "chat") {
                            return (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Position
                                </label>
                                <div className="relative">
                                  <select
                                    value={editForm.position}
                                    onChange={(e) =>
                                      setEditForm({
                                        ...editForm,
                                        position: e.target.value,
                                      })
                                    }
                                    className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-gray-500/50 focus:border-gray-500 transition-all duration-200 appearance-none cursor-pointer"
                                  >
                                    <option value="bottom-right">Bottom Right</option>
                                    <option value="bottom-left">Bottom Left</option>
                                    <option value="top-right">Top Right</option>
                                    <option value="top-left">Top Left</option>
                                  </select>
                                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                                </div>
                                <div className="mt-4">
                                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                    <label className="flex items-start justify-between cursor-pointer">
                                      <div className="flex-1 pr-4">
                                        <p className="text-sm font-medium text-gray-800 mb-1">
                                          Show Footer
                                        </p>
                                        <p className="text-xs text-gray-600 leading-relaxed">
                                          Enable to show &apos;we run on [Logo]&apos; footer in the chat widget
                                        </p>
                                      </div>
                                      <div className="relative flex-shrink-0">
                                        <input
                                          type="checkbox"
                                          checked={editForm.footerEnabled}
                                          onChange={() =>
                                            setEditForm({
                                              ...editForm,
                                              footerEnabled: !editForm.footerEnabled,
                                            })
                                          }
                                          className="sr-only"
                                        />
                                        <div
                                          className={`w-12 h-6 ${editForm.footerEnabled
                                            ? "bg-black"
                                            : "bg-gray-300"
                                            } rounded-full transition-colors duration-200`}
                                        ></div>
                                        <div
                                          className="dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 shadow-sm"
                                          style={{
                                            transform: editForm.footerEnabled
                                              ? "translateX(24px)"
                                              : "translateX(0)",
                                          }}
                                        ></div>
                                      </div>
                                    </label>
                                  </div>
                                </div>
                              </div>
                            );
                          } else if (widget?.widget_type === "faq") {
                            return (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                  Enable Contact Support Button
                                </label>
                                <div className="flex items-center">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setEditForm({
                                        ...editForm,
                                        supportEnabled: !editForm.supportEnabled,
                                      })
                                    }
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 ${editForm.supportEnabled
                                      ? "bg-black"
                                      : "bg-gray-300"
                                      }`}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${editForm.supportEnabled
                                        ? "translate-x-6"
                                        : "translate-x-1"
                                        }`}
                                    />
                                  </button>
                                  <span className="ml-3 text-sm text-gray-600">
                                    {editForm.supportEnabled ? "Enabled" : "Disabled"}
                                  </span>
                                </div>
                                <p className="mt-2 text-xs text-gray-500">
                                  When enabled, users can contact your support team
                                  directly from the FAQ widget
                                </p>

                                {/* Contact Position Dropdown - Only show when support is enabled */}
                                {editForm.supportEnabled && (
                                  <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Contact Button Position
                                    </label>
                                    <div className="relative">
                                      <select
                                        value={editForm.contactPosition || "bottom"}
                                        onChange={(e) =>
                                          setEditForm({
                                            ...editForm,
                                            contactPosition: e.target.value,
                                          })
                                        }
                                        className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-gray-500/50 focus:border-gray-500 transition-all duration-200 appearance-none cursor-pointer"
                                      >
                                        <option value="top">Contact at TOP</option>
                                        <option value="bottom">Contact at END</option>
                                      </select>
                                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                                    </div>
                                    <p className="mt-2 text-xs text-gray-500">
                                      Position the &quot;Can&apos;t find what you&apos;re looking for?&quot; section
                                    </p>
                                  </div>
                                )}

                                {/* Accordion Icon Style Toggle */}
                                <div className="mt-4">
                                  <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Accordion Icon Style
                                  </label>
                                  <div className="flex items-center">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setEditForm({
                                          ...editForm,
                                          accordionIconStyle: editForm.accordionIconStyle === "arrow" ? "plus-minus" : "arrow",
                                        })
                                      }
                                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 ${editForm.accordionIconStyle === "arrow"
                                        ? "bg-black"
                                        : "bg-gray-300"
                                        }`}
                                    >
                                      <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${editForm.accordionIconStyle === "arrow"
                                          ? "translate-x-6"
                                          : "translate-x-1"
                                          }`}
                                      />
                                    </button>
                                    <span className="ml-3 text-sm text-gray-600">
                                      {editForm.accordionIconStyle === "arrow" ? "↓ Arrow" : "+/− Plus/Minus"}
                                    </span>
                                  </div>
                                  <p className="mt-2 text-xs text-gray-500">
                                    Toggle to switch between Arrow or Plus/Minus icons
                                  </p>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>

                    {/* Right: Live Preview */}
                    <div className="md:col-span-1 p-2 sm:p-4 flex flex-col">
                      <div className="flex justify-center items-center min-h-[500px] py-4">

                        <div className="border border-gray-200 rounded-lg bg-gray-50 p-3 sm:p-5 w-full max-w-full">
                          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 w-full max-w-full overflow-visible">
                            <div className="p-0">
                              {(() => {
                                const widget = widgets.find((w) => w.widget_id === editingWidgetId);
                                if (widget?.widget_type === "faq") {
                                  return (
                                    <div className="space-y-2 sm:space-y-4 px-3 py-3 sm:px-4 sm:py-4 md:px-6 md:py-6">
                                      <div className="text-center pt-2 mb-2">
                                        <div
                                          className="text-[10px] sm:text-xs font-semibold tracking-wider mb-1"
                                          style={{
                                            color: editForm.primaryColor || "#3B82F6",
                                          }}
                                        >
                                          GOT QUESTIONS?
                                        </div>
                                        <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 px-2">
                                          {editForm.title || "Frequently Asked Questions"}
                                        </h3>
                                      </div>
                                      <div className="border-t border-gray-200 my-2 sm:my-3"></div>
                                      {/* Search Bar */}
                                      <div className="mb-2">
                                        <input
                                          type="text"
                                          className="w-full rounded-lg border border-gray-200 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                                          placeholder="Search frequently asked questions..."
                                          disabled
                                        />
                                      </div>
                                      {/* FAQ Items (reduced for compact preview) */}
                                      {(() => {
                                        const faqItems = [
                                          {
                                            q: "What is the AEO Platform and how does it work?",
                                            a: "The AEO Platform is a revolutionary tool that enhances your website&apos;s SEO through advanced AI and RAG technology.",
                                          },
                                          {
                                            q: "How does the AEO Platform improve my website&apos;s SEO?",
                                            a: "It automatically generates SEO-optimized content and provides real-time analytics to boost your search engine rankings.",
                                          },
                                          {
                                            q: "What is RAG technology and how does the AEO Platform use it?",
                                            a: "RAG (Retrieval-Augmented Generation) allows our AI to access your specific knowledge base, providing accurate and context-aware answers.",
                                          },
                                        ];

                                        const FaqItem = ({ item, index }) => {
                                          const isOpen = openAccordion === index;
                                          const Icon =
                                            editForm.accordionIconStyle === "arrow" ? (
                                              <ChevronDown
                                                className={`h-5 w-5 text-gray-500 transition-transform duration-300 ${isOpen ? "transform rotate-180" : ""
                                                  }`}
                                              />
                                            ) : isOpen ? (
                                              <Minus className="h-5 w-5 text-gray-500" />
                                            ) : (
                                              <Plus className="h-5 w-5 text-gray-500" />
                                            );

                                          return (
                                            <div className="text-xs sm:text-sm border-b border-gray-200 last:border-b-0">
                                              <button
                                                onClick={() =>
                                                  setOpenAccordion(
                                                    isOpen ? null : index
                                                  )
                                                }
                                                className="w-full font-normal text-gray-700 flex items-center justify-between px-2 sm:px-3 py-2 sm:py-3 text-left"
                                              >
                                                <span className="text-xs sm:text-sm pr-2">{item.q}</span>
                                                <span className="flex-shrink-0">{Icon}</span>
                                              </button>
                                              {isOpen && (
                                                <div className="px-2 sm:px-3 pb-2 sm:pb-3 text-gray-600 text-xs sm:text-sm">
                                                  {item.a}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        };

                                        const ContactSupportSection = () =>
                                          editForm.supportEnabled && (
                                            <div className="mt-3 sm:mt-4 text-center px-2">
                                              <div className="text-[10px] sm:text-xs text-gray-500 mb-2">
                                                Can&apos;t find what you&apos;re looking for?
                                              </div>
                                              <button
                                                className="px-3 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-xs text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                                                style={{
                                                  background: `linear-gradient(90deg, ${editForm.primaryColor || "#E91E63"
                                                    }, ${editForm.secondaryColor ||
                                                    "#1A1A2E"
                                                    })`,
                                                }}
                                              >
                                                Contact Our Support Team
                                              </button>
                                            </div>
                                          );

                                        return (
                                          <>
                                            {editForm.contactPosition === "top" && (
                                              <ContactSupportSection />
                                            )}
                                            <div className="space-y-2">
                                              {faqItems.map((item, index) => (
                                                <FaqItem
                                                  key={index}
                                                  item={item}
                                                  index={index}
                                                />
                                              ))}
                                            </div>
                                            <div className="border-t border-gray-200 my-2 sm:my-3"></div>
                                            <div className="pt-2 text-center">
                                              <div className="flex items-center justify-center mb-2">
                                                <div className="flex items-center justify-center space-x-1">
                                                  <button className="px-1.5 sm:px-2 py-1 text-[10px] sm:text-xs text-gray-400 hover:text-gray-600 disabled:opacity-50">
                                                    Prev
                                                  </button>
                                                  <button
                                                    className="px-1.5 sm:px-2 py-1 text-[10px] sm:text-xs text-white rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center font-medium"
                                                    style={{
                                                      background: `linear-gradient(90deg, ${editForm.primaryColor ||
                                                        "#E91E63"
                                                        }, ${editForm.secondaryColor ||
                                                        "#1A1A2E"
                                                        })`,
                                                      border: "none",
                                                    }}
                                                  >
                                                    1
                                                  </button>
                                                  <button className="px-1.5 sm:px-2 py-1 text-[10px] sm:text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded">
                                                    2
                                                  </button>
                                                  <button className="px-1.5 sm:px-2 py-1 text-[10px] sm:text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded">
                                                    3
                                                  </button>
                                                  <button className="px-1.5 sm:px-2 py-1 text-[10px] sm:text-xs text-gray-600 hover:text-gray-800">
                                                    Next
                                                  </button>
                                                </div>
                                              </div>
                                              {editForm.contactPosition !==
                                                "top" && <ContactSupportSection />}
                                            </div>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  );
                                } else if (widget?.widget_type === "chat") {
                                  return (
                                    <div className="w-full max-w-[340px] mx-auto h-[500px] bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden flex flex-col">
                                        {/* Header */}
                                        <div
                                          className="flex items-center justify-between px-5 py-3 text-white"
                                          style={{
                                            background: `linear-gradient(135deg, ${editForm.primaryColor || "#3B82F6"
                                              }, ${editForm.secondaryColor || "#6366F1"})`,
                                          }}
                                        >
                                          <span className="font-semibold text-base">
                                            {editForm.title || "Customer Support"}
                                          </span>
                                          <button className="p-1.5 hover:opacity-80 transition-opacity">
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="20"
                                              height="20"
                                              viewBox="0 0 24 24"
                                              stroke="white"
                                              strokeWidth="2"
                                              fill="none"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            >
                                              <line x1="18" y1="6" x2="6" y2="18" />
                                              <line x1="6" y1="6" x2="18" y2="18" />
                                            </svg>
                                          </button>
                                        </div>

                                        {/* Greeting */}
                                        <div className="px-3 sm:px-5 py-4">
                                          <div className="bg-white border border-gray-200 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-gray-700 text-xs sm:text-sm shadow-sm">
                                            Hello! How can we help you today?
                                          </div>
                                        </div>

                                        {/* Chat area (empty for preview) */}
                                        <div className="flex-1 flex flex-col justify-end px-3 sm:px-5"></div>

                                        <div className="flex items-center gap-2 px-3 sm:px-5 mb-3 text-xs sm:text-sm">
                                          <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="flex-shrink-0"
                                          >
                                            <path
                                              d="M12 12c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19c0 0.55 0.45 1 1 1h12c0.55 0 1-0.45 1-1v-1.5c0-2.33-4.67-3.5-7-3.5z"
                                              fill={editForm.primaryColor}
                                            />
                                          </svg>
                                          <span
                                            className="font-medium cursor-pointer bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] bg-clip-text text-transparent"
                                            style={{
                                              "--primary": editForm.primaryColor || "#3B82F6",
                                              "--secondary": editForm.secondaryColor || "#6366F1",
                                              backgroundImage: `linear-gradient(90deg, ${editForm.primaryColor || "#3B82F6"}, ${editForm.secondaryColor || "#6366F1"})`,
                                              WebkitBackgroundClip: "text",
                                              WebkitTextFillColor: "transparent",
                                              backgroundClip: "text",
                                              color: "transparent",
                                            }}
                                          >
                                            Talk to Human
                                          </span>
                                        </div>
                                        <div className="relative w-full px-3 sm:px-5 pb-4">
                                          <input
                                            type="text"
                                            placeholder="Type your message here..."
                                            className="w-full text-xs sm:text-sm bg-gray-50 border border-gray-200 rounded-full pl-3 sm:pl-4 pr-10 sm:pr-12 py-2 sm:py-2.5 outline-none"
                                            disabled
                                          />
                                          <button
                                            className="absolute right-4 sm:right-6 top-1/3 -translate-y-1/2 w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shadow-sm"
                                            style={{
                                              background: `linear-gradient(90deg, ${editForm.primaryColor || "#3B82F6"
                                                }, ${editForm.secondaryColor || "#6366F1"
                                                })`,
                                            }}
                                            disabled
                                          >
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="16"
                                              height="16"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="white"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            >
                                              <line x1="22" y1="2" x2="11" y2="13" />
                                              <polygon points="22,2 15,22 11,13 2,9 22,2" />
                                            </svg>
                                          </button>
                                        </div>
                                        {editForm.footerEnabled && (
                                          <div className="px-5 py-2.5 bg-white border-t border-gray-100 flex items-center justify-center gap-1.5 text-[10px] text-gray-400">
                                            <span className="font-medium">we run on</span>
                                            <img src="/logo/uttik_logo_b.png" className="h-4 w-auto object-contain" alt="Uttik Logo" />
                                          </div>
                                        )}
                                      </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>


                  <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowDeleteConfirm(true)
                      }}
                      className="bg-white border-2 border-gray-300 text-black font-bold px-4 sm:px-6 py-2 sm:py-2 text-sm rounded-xl hover:bg-gray-50 hover:border-black transition-all shadow-sm flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      Delete
                    </button>
                    <div className="flex justify-end space-x-2 sm:space-x-3 ">
                      <button
                        type="button"
                        onClick={() => {
                          setShowEditModal(false);
                          setEditingWidgetId(null);
                        }}
                        className="px-4 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm font-medium text-white bg-black rounded-xl hover:bg-gray-800 transition-all duration-200"
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      {
        showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)}></div>
            <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full border-2 border-gray-200 animate-in fade-in zoom-in duration-200">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <AlertTriangle className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Delete Widget?</h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete <span className="font-semibold text-gray-900">&quot;{selectedWidget?.configuration?.title}&quot;</span>? This action cannot be undo.
                </p>
                <div className="flex gap-3 w-full">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 bg-white border-2 border-gray-300 text-gray-700 font-bold py-3 px-6 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteConfirm(selectedWidget?.widget_id)}
                    className="flex-1 bg-black text-white font-bold py-3 px-6 rounded-xl hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-5 h-5" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
}

export default WidgetManager;
